"use client";

import type { CatalogScope } from "@/lib/host-panel/catalog-types";

/**
 * Catalog empty state (handoff §7.1, criterion 17.7), with literal copy from the prototype.
 *
 * What separates the cases is whether a filter is active: without one, the emptiness is a fact
 * about the list and the way out is the other scope; with one, it follows from a choice and the
 * way out is undoing it.
 */

export type CatalogEmptyProps = {
  scope: CatalogScope;
  /** Whether any facet filter or taxonomy slice is active. */
  isFiltering: boolean;
  onGoToScope: (scope: CatalogScope) => void;
  onClearAll: () => void;
};

export function CatalogEmpty({ scope, isFiltering, onGoToScope, onClearAll }: CatalogEmptyProps) {
  const state = resolveEmptyState(scope, isFiltering);

  return (
    /*
     * Fades in rather than arriving by scroll reveal: the message answers a filter just applied
     * and has to appear where the person is already looking.
     */
    <div className="mt-[clamp(24px,3vw,40px)] flex flex-col items-start gap-3 motion-safe:animate-[tb-fade-in_var(--tb-duration-3)_var(--tb-ease-linen)_both]">
      <p className="text-[14px] leading-[1.6] text-[var(--fg-muted)] [text-wrap:pretty]">
        {state.message}
      </p>

      <button
        type="button"
        onClick={() => (state.action === "clear" ? onClearAll() : onGoToScope(state.action))}
        className="min-h-[var(--tb-touch-min)] cursor-pointer border-b border-tb-green text-[14px] text-tb-green"
      >
        {state.cta}
      </button>
    </div>
  );
}

type EmptyState = {
  message: string;
  cta: string;
  action: CatalogScope | "clear";
};

export function resolveEmptyState(scope: CatalogScope, isFiltering: boolean): EmptyState {
  if (isFiltering) {
    return { message: "Nenhuma peça neste filtro.", cta: "Ver todos", action: "clear" };
  }

  if (scope === "dentro") {
    return {
      message: "A sua lista ainda não tem peças.",
      cta: "Escolher peças do catálogo",
      action: "fora",
    };
  }

  return {
    message: "O catálogo habilitado está carregando.",
    cta: "Ver a minha lista",
    action: "dentro",
  };
}
